<template>
  <div v-if="userHasPermissions('provider.practitioner.specialty.view')">
    <section-title content="Información de Especialidad" :has-line="true" />
    <div v-if="value.length > 0" class="specialty-information-list mt-3">
      <callout
        v-for="(specialty, idx) in value"
        :key="idx"
        class="mb-2"
        :has-toggle="false"
        :opened="
          !disabled &&
            editMode &&
            editInfo.data != null &&
            editInfo.index == idx
        "
      >
        <div class="row mt-2 mb-2">
          <div class="col-sm-12 col-md-2 col-lg-2 cell sd">
            <div class="data ml-3">
              <p class="bold">
                {{ specialty.practitionerTypeName }}
              </p>
            </div>
          </div>
          <div class="col-sm-12 col-md-4 col-lg-4 cell ml-3 ml-md-0 ml-sm-3">
            <div class="data">
              <span>Universidad:</span>
              <p>{{ specialty.collegeName }}</p>
            </div>
          </div>
          <div class="col-sm-12 col-md-2 col-lg-2 cell ml-3 ml-md-0 ml-sm-3">
            <div class="data">
              <span>Fecha Desde:</span>
              <p>{{ specialty.startDate | date }}</p>
            </div>
          </div>
          <div class="col-sm-12 col-md-2 col-lg-2 cell ml-3 ml-md-0 ml-sm-3">
            <div class="data">
              <span>Fecha Hasta:</span>
              <p>{{ specialty.endDate | date }}</p>
            </div>
          </div>
          <div
            v-if="!disabled && canPerformActions"
            class="col-sm-12 col-md-2 col-lg-2 cell actions ml-3 ml-md-0 ml-sm-3"
          >
            <drop-down
              :data-item="idx"
              :items="getAvailableActions(specialty, idx)"
            />
          </div>
        </div>
        <div class="splitter">
          <div class="col-sm-12" />
        </div>
        <div class="row mt-2 mb-2">
          <div class="col-sm-12 col-md-2 cell">
            <div class="data ml-3">
              <span>Especialidad:</span>
              <p>{{ specialty.specialtyTypeName }}</p>
            </div>
          </div>
          <div class="col-md-10 col-sm-12 cell ml-3 ml-md-0 ml-sm-3">
            <div
              v-for="disciplineType in specialty.disciplineTypes"
              :key="disciplineType.id"
              class="badge"
            >
              <p>{{ disciplineType.name }}</p>
            </div>
          </div>
        </div>

        <template v-if="editMode && editInfo.index == idx" #edition>
          <specialty-information-add-edit
            v-model="editInfo.data"
            :custom-error-message="addEditCustomErrorMessage"
            :used-specialties="existingSpecialties"
            @on-cancel="onSpecialtyCanceled"
            @on-saved="onSpecialtySaved"
          />
        </template>
      </callout>
      <div v-if="!disabled" class="row">
        <div class="col">
          <action-button
            v-if="editInfo.data == null && canAddSpecialty"
            class="float-right"
            i="fas fa-file-plus"
            variant="success"
            @click="newSpecialty"
          >
            Añadir
          </action-button>
        </div>
      </div>
    </div>
    <div v-else class="specialty-information-list mt-3">
      <callout
        v-if="
          value.length == 0 &&
            canAddSpecialty &&
            editInfo.data == null &&
            !disabled
        "
        class-main="py-0 py-md-3 py-sm-0 px-4 px-md-0 px-sm-4"
        :has-toggle="false"
      >
        <div class="row d-flex justify-content-center align-items-center">
          <action-button
            class="border w-sm-100 w-md-40 mt-4 mt-md-0 mt-sm-4"
            i="fas fa-file-plus color-primary-1"
            text="Presione aquí para añadir Especialidad"
            @click="newSpecialty"
          />
        </div>
      </callout>
    </div>

    <callout
      class="mb-2"
      :has-toggle="false"
      :opened="!disabled && createMode"
      :show-container="false"
    >
      <template #edition>
        <specialty-information-add-edit
          v-model="editInfo.data"
          :custom-error-message="addEditCustomErrorMessage"
          :used-specialties="existingSpecialties"
          @on-cancel="onSpecialtyCanceled"
          @on-saved="onSpecialtySaved"
        />
      </template>
    </callout>
    <error
      v-if="displayErrorMessage"
      :message="customErrorMessage"
      variant="no-border"
    />
  </div>
</template>

<script>
import Callout from 'custom-callout';
import ActionButton from 'ActionButton';
import SectionTitle from 'SectionTitle';
import Error from 'custom-error';
import DropDown from 'dropdown';
import SpecialtyInformationAddEdit from './SpecialtyInformationAddEdit';

import { mapState } from 'vuex';

import alert from '@/mixins/alert.mixin';

import { getSpecialtyDisciplineTypes as _getSpecialtyDisciplineTypes } from '@/modules/shared/code.api.js';

export default {
  name: 'SpecialtyInformationList',
  components: {
    Callout,
    ActionButton,
    SectionTitle,
    Error,
    DropDown,
    SpecialtyInformationAddEdit,
  },
  mixins: [alert],
  props: {
    value: {
      type: Array,
      required: true,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    practitionerId: {
      type: Number,
      default: () => null,
    },
    onSaving: {
      type: Function,
      default: null,
    },
    onDeleting: {
      type: Function,
      default: null,
    },
    addEnabled: {
      type: Boolean,
      default: true,
    },
    editEnabled: {
      type: Boolean,
      default: true,
    },
    deleteEnabled: {
      type: Boolean,
      default: true,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    customErrorMessage: {
      type: String,
      default: () => null,
    },
  },
  data() {
    return {
      editInfo: {
        index: 0,
        data: null,
      },
      editMode: false,
      createMode: false,
      addEditCustomErrorMessage: null,
      displayErrorMessage: !this.isValid,
    };
  },
  computed: {
    ...mapState('$_user', ['user']),
    canAddSpecialty() {
      return this.addEnabled;
    },
    canPerformActions() {
      return this.editEnabled || this.deleteEnabled;
    },
    existingSpecialties() {
      return this.value.map((item) => ({
        id: item.specialtyTypeId,
        name: item.specialtyName,
        practitionerTypeId: item.practitionerTypeId,
      }));
    },
  },
  watch: {
    value: function(newValue) {
      if (newValue.length == 0 && !this.disabled) {
        this.newSpecialty();
      }
    },
    isValid: function(newValue) {
      this.displayErrorMessage = !newValue;
    },
  },
  methods: {
    getAvailableActions() {
      let actions = [
        {
          name: 'edit',
          label: 'Editar',
          icon: 'fas fa-edit color-primary-1',
          click: this.onEdit,
          visible: !this.disabled && this.editEnabled,
        },
        {
          name: 'delete',
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-danger',
          click: this.onDelete,
          visible:
            !this.disabled && this.deleteEnabled && this.value.length > 1,
        },
      ];

      return actions;
    },
    newSpecialty() {
      this.displayErrorMessage = false;

      this.createMode = true;
      this.editMode = false;
      this.editInfo = this.getNewSpecialtyModel();
    },
    onEdit(dataItemIndex) {
      this.editMode = true;
      this.createMode = false;

      this.editInfo = {
        index: dataItemIndex,
        data: this.value[dataItemIndex],
      };
    },
    async onDelete(dataItemIndex) {
      await this.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea eliminar la especialidad seleccionada?',
        'Sí',
        'No'
      ).then(async (answer) => {
        if (answer) {
          let isValid = true;

          if (this.onDeleting != null)
            isValid = await this.onDeleting(
              this.value[dataItemIndex].practitionerId,
              this.value[dataItemIndex].specialtyTypeId
            );

          if (isValid) {
            this.value.splice(dataItemIndex, 1);
          }
        }
      });
    },
    async onSpecialtySaved(savedSpecialty) {
      let isValid = true;

      //Refresh the disciplines of the saved specialty
      await _getSpecialtyDisciplineTypes(savedSpecialty.specialtyTypeId).then(
        (resp) => {
          savedSpecialty.disciplineTypes = resp.data;
        }
      );

      //Check for duplicates (only for new records)
      if (savedSpecialty.id == null) {
        //TODO: Check for duplicates
      }

      if (this.onSaving != null) {
        isValid = await this.onSaving(savedSpecialty);
      }

      if (isValid) {
        if (savedSpecialty.isNew) {
          savedSpecialty.isNew = false;
          this.value.push(savedSpecialty);
        } else {
          var modelIndex = this.value.findIndex(
            (x) =>
              x.practitionerTypeId ==
                savedSpecialty.originalPractitionerType.id &&
              x.specialtyTypeId == savedSpecialty.originalSpecialtyType.id
          );

          this.value[modelIndex] = savedSpecialty;
        }

        this.clearEditCreate();
      }
    },
    async onSpecialtyCanceled() {
      this.clearEditCreate();
    },
    clearEditCreate() {
      this.createMode = false;
      this.editMode = false;

      this.editInfo = {
        index: 0,
        data: null,
      };
    },
    getNewSpecialtyModel() {
      const newSpecialtyModel = {
        data: {
          id: null,
          practitionerId: this.practitionerId,
          practitionerType: null,
          practitionerTypeId: null,
          practitionerTypeName: null,
          college: null,
          collegeId: null,
          collegeName: null,
          company: null,
          companyId:
            this.user.profile.companyid != undefined
              ? parseInt(this.user.profile.companyid)
              : null,
          companyName: null,
          specialtyType: null,
          specialtyTypeId: null,
          specialtyTypeName: null,
          startDate: null,
          endDate: null,
          isNew: true,
          disciplineTypes: [],
        },
      };

      return newSpecialtyModel;
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~@/shared/scss/config/fonts.scss';
@import '~@/shared/scss/config/colors.scss';

.specialty-information-list .splitter div {
  border-bottom: 1px solid #d9e5df;
  margin-top: 10px;
  margin-bottom: 10px;
  width: 100%;
}

.specialty-information-list .cell {
  display: table-cell !important;
  vertical-align: middle;
}

.specialty-information-list .cell.sd .data {
  width: 100%;
}

.specialty-information-list .cell.sd .data p {
  margin-top: 5px;
}

.specialty-information-list .cell span {
  color: map-get($colors, 'gray-6');
  font-size: map-get($fontsSize, 'font-14');
}

.specialty-information-list .cell p {
  color: map-get($colors, 'black');
  font-size: map-get($fontsSize, 'font-16');
  margin-bottom: 0;
}

.specialty-information-list .cell p:last-child {
  font-weight: 500;
}

.specialty-information-list .cell {
  margin-top: auto;
  margin-bottom: auto;
  padding-left: 0px;
  padding-right: 0px;
}

.specialty-information-list .cell.sd {
  margin-top: 4.5px;
}

.specialty-information-list .cell.actions {
  text-align: center;
}

.badge {
  background-color: #b5c3bc;
  font-size: 16px;
  line-height: 13px;
  border-radius: 20px;
  padding: 10px;
  margin-right: 15px;
  margin-bottom: 10px;
  white-space: unset !important;
}

.badge p {
  color: white !important;
}

@media (max-width: 768px) {
  .specialty-information-list .cell.actions {
    text-align: left !important;
  }

  .specialty-information-list .cell p:last-child {
    margin-top: 0;
    margin-bottom: 10px;
  }
}

@media (max-width: 992px) {
  .specialty-information-list .cell.hidden {
    display: none !important;
  }
}

@media (min-width: 992px) and (max-width: 1145px) {
  .specialty-information-list .cell.hidden {
    display: none !important;
  }
}
</style>
